import { RouterLink as NextLink } from "../../router";
import type { InsightCard as InsightCardData } from "@excellent/core";
import { Card, CardContent } from "../../card";
import { Sparkline } from "../sparkline";

/** Tone → token mapping for the left-edge dot. */
const TONE_DOT: Record<NonNullable<InsightCardData["tone"]>, string> = {
  neutral: "bg-muted-foreground/40",
  info: "bg-info",
  warning: "bg-warning",
  danger: "bg-danger",
  success: "bg-success",
};

/**
 * One synthesized claim. Insight cards are the *analysis* layer of a
 * dashboard — sentences a senior practitioner would say out loud, not
 * raw numbers. See docs/dashboard/PRD.md §4.4 and Appendix C.
 */
export function InsightCard({ insight }: { insight: InsightCardData }) {
  const dot = TONE_DOT[insight.tone ?? "neutral"];

  const body = (
    <CardContent className="flex items-start gap-3 p-4">
      <span
        className={`mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full ${dot}`}
        aria-hidden
      />
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-baseline justify-between gap-3">
          <span className="type-body text-foreground">{insight.title}</span>
          {insight.metric ? (
            <span className="type-body tabular-nums text-muted-foreground">
              {insight.metric}
            </span>
          ) : null}
        </div>
        {insight.body ? (
          <p className="type-body text-muted-foreground">{insight.body}</p>
        ) : null}
        {insight.visual ? <InsightVisual visual={insight.visual} /> : null}
      </div>
    </CardContent>
  );

  if (insight.href) {
    return (
      <NextLink
        href={insight.href}
        className="block rounded-md transition-colors hover:bg-accent/20"
      >
        <Card className="py-0">{body}</Card>
      </NextLink>
    );
  }
  return <Card>{body}</Card>;
}

function InsightVisual({
  visual,
}: {
  visual: NonNullable<InsightCardData["visual"]>;
}) {
  if (visual.kind === "spark") {
    return (
      <Sparkline
        values={visual.data}
        color={visual.accentColor}
        height={24}
        padding={2}
        strokeOpacity={0.8}
        className="h-6 w-full"
        ariaHidden
      />
    );
  }
  return <InsightBarRow segments={visual.segments} />;
}

function InsightBarRow({
  segments,
}: {
  segments: { label: string; value: number; color?: string }[];
}) {
  const total = segments.reduce((acc, s) => acc + Math.max(0, s.value), 0);
  if (total <= 0) return null;
  return (
    <div className="space-y-1">
      <div className="flex h-2 overflow-hidden rounded-full bg-muted/40">
        {segments.map((s) => (
          <div
            key={s.label}
            title={`${s.label}: ${s.value}`}
            style={{
              width: `${(Math.max(0, s.value) / total) * 100}%`,
              backgroundColor: s.color ?? "var(--chart-4)",
            }}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-3 type-body text-muted-foreground">
        {segments.map((s) => (
          <span key={s.label} className="inline-flex items-center gap-1.5">
            <span
              className="inline-block h-1.5 w-1.5 rounded-full"
              style={{ backgroundColor: s.color ?? "var(--chart-4)" }}
              aria-hidden
            />
            <span>{s.label}</span>
            <span className="tabular-nums">{s.value}</span>
          </span>
        ))}
      </div>
    </div>
  );
}
